/**
 * 详细记录折叠面板容器组件
 */
import React, { useState } from 'react';
import { Collapse, Typography, Space, Badge } from 'antd';
import {
  UserOutlined,
  ToolOutlined,
  GoldOutlined
} from '@ant-design/icons';
import WorkTimePanel from './WorkTimePanel';
import EquipmentPanel from './EquipmentPanel';
import MaterialPanel from './MaterialPanel';
import { formatMoney } from '../../../utils/budgetUtils';

const { Panel } = Collapse;
const { Text } = Typography;

interface DetailRecordsProps {
  projectId: string;
  stats: any;
  isMobile?: boolean;
}

const DetailRecords: React.FC<DetailRecordsProps> = ({
  projectId,
  stats,
  isMobile = false
}) => {
  const [activeKey, setActiveKey] = useState<string | string[]>([]);

  // 获取各类记录数量和成本
  const workTimeCount = stats?.workTime?.recordCount || 0;
  const workTimeCost = stats?.summary?.costBreakdown?.workTimeCost || 0;
  
  const equipmentCount = stats?.equipment?.recordCount || 0;
  const equipmentCost = stats?.summary?.costBreakdown?.equipmentCost || 0;
  
  const materialCount = stats?.material?.recordCount || 0;
  const materialCost = stats?.summary?.costBreakdown?.materialCost || 0;

  // 渲染面板头部
  const renderPanelHeader = (
    icon: React.ReactNode,
    label: string,
    count: number,
    cost: number,
    color: string
  ) => (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      width: '100%',
      paddingRight: 8
    }}>
      <Space size={8}>
        {icon}
        <Text style={{ fontSize: isMobile ? 13 : 14 }}>{label}</Text>
        <Badge 
          count={count} 
          style={{ backgroundColor: '#f0f0f0', color: '#666' }}
          overflowCount={999}
        />
      </Space>
      <Text strong style={{ color, fontSize: isMobile ? 13 : 14 }}>
        {formatMoney(cost, false)}
      </Text>
    </div>
  );

  // 检查哪些面板是激活的
  const isActive = (key: string) => {
    if (Array.isArray(activeKey)) {
      return activeKey.includes(key);
    }
    return activeKey === key;
  };

  return (
    <div style={{ marginBottom: isMobile ? 16 : 24 }}>
      <Typography.Title level={5} style={{ marginBottom: 16, fontSize: isMobile ? 14 : 16 }}>
        详细记录
      </Typography.Title>
      
      <Collapse
        activeKey={activeKey}
        onChange={setActiveKey}
        expandIconPosition="start"
        style={{ backgroundColor: '#fff' }}
      >
        {/* 工时记录面板 */}
        <Panel
          key="worktime"
          header={renderPanelHeader(
            <UserOutlined style={{ color: '#1677ff' }} />,
            '工时记录',
            workTimeCount,
            workTimeCost,
            '#1677ff'
          )}
          style={{ marginBottom: 8 }}
        >
          <WorkTimePanel
            projectId={projectId}
            isMobile={isMobile}
            active={isActive('worktime')}
          />
        </Panel>

        {/* 设备记录面板 */}
        <Panel
          key="equipment"
          header={renderPanelHeader(
            <ToolOutlined style={{ color: '#52c41a' }} />,
            '设备记录',
            equipmentCount,
            equipmentCost,
            '#52c41a'
          )}
          style={{ marginBottom: 8 }}
        >
          <EquipmentPanel
            projectId={projectId}
            isMobile={isMobile}
            active={isActive('equipment')}
          />
        </Panel>

        {/* 材料记录面板 */}
        <Panel
          key="material"
          header={renderPanelHeader(
            <GoldOutlined style={{ color: '#fa8c16' }} />,
            '材料记录',
            materialCount,
            materialCost,
            '#fa8c16'
          )}
        >
          <MaterialPanel
            projectId={projectId}
            isMobile={isMobile}
            active={isActive('material')}
          />
        </Panel>
      </Collapse>
    </div>
  );
};

export default DetailRecords;
